import { utcIsoToLocal, zoneFor } from '@/lib/delay/time';

/**
 * The delay-limits rules, client side (brief §1b decisions 3-4 and 11): a MIRROR of the
 * engine seams `resolution-engine:delayExceedsLimit@v1` and `delayHotelVerdict@v1`
 * (same strict / ≥ semantics, same reason codes), so step 2 answers while the operator
 * types. cases stays authoritative: a disagreement (a policy edited mid-declaration, the
 * UI and engine zone tables drifting) comes back as `400 delay_exceeds_limit` or as the
 * case's own care verdict — never as wrong money.
 */

const DAY_MS = 86_400_000;

export interface IDelayLimitCheck {
  exceeds: boolean;
  delayMinutes: number;
  limitMinutes: number;
}

/**
 * `ETD − STD > limitHours × 60 min` — strict: a delay of exactly the limit is accepted.
 * `std` / `etd` are instants (ISO with offset); an unparseable one never exceeds.
 */
export function delayExceedsLimit(std: string, etd: string, limitHours: number): IDelayLimitCheck {
  const delayMs = Date.parse(etd) - Date.parse(std);
  const limitMinutes = Math.round(limitHours * 60);
  return { exceeds: delayMs > limitMinutes * 60_000, delayMinutes: Math.round(delayMs / 60_000), limitMinutes };
}

export type DelayHotelReason = 'DISABLED' | 'BEFORE_THRESHOLD' | 'NEXT_DAY_AFTER_HOUR';

export interface IDelayHotelVerdict {
  /** `enabled && etd ≥ threshold`. */
  hotel: boolean;
  /** (STD's local day + 1) at `afterLocalHour`:00 in the airport's zone, as an instant. */
  thresholdUtc: string;
  /** The IANA zone used (`UTC` with no departure airport). */
  timeZone: string;
  reason: DelayHotelReason;
}

/** Offset of `zone` at instant `ms` (local − UTC), in ms, read through the shared wall-clock helper. */
function offsetMs(ms: number, zone: string): number {
  const minute = Math.floor(ms / 60_000) * 60_000;
  return Date.parse(`${utcIsoToLocal(new Date(minute).toISOString(), zone)}:00Z`) - minute;
}

/**
 * The instant a wall clock (`wall`: its fields read as UTC) shows in `zone`, DST-correct
 * by the engine's convention: a wall time that occurs twice (clocks back) → its FIRST
 * occurrence; one that does not exist (clocks forward) → resolved forward, to the jump.
 * Not `localToUtcIso` (`lib/delay/time.ts`), which answers differently at exactly those
 * two edges (Santiago's skipped 00:00 → before the jump; a repeated hour east of UTC →
 * its second occurrence).
 */
function wallToUtcMs(wall: number, zone: string): number {
  // The offsets two days either side bracket any transition near this wall time.
  const candidates = [offsetMs(wall - 2 * DAY_MS, zone), offsetMs(wall + 2 * DAY_MS, zone)].map((o) => wall - o);
  const real = candidates.filter((c) => c + offsetMs(c, zone) === wall);
  return real.length ? Math.min(...real) : Math.max(...candidates);
}

/**
 * The next-day delay hotel rule: a hotel when the new departure falls on the next local
 * day at or after `afterLocalHour`:00 at the departure airport — or any later day. The
 * basis is the declared ETD (cases uses the worst one; for a new declaration they are
 * the same). `null` while STD or ETD is not a usable instant.
 */
export function nextDayHotelVerdict(ctx: {
  std: string;
  etd: string;
  originIata: string | null | undefined;
  enabled: boolean;
  afterLocalHour: number;
}): IDelayHotelVerdict | null {
  const stdMs = Date.parse(ctx.std);
  const etdMs = Date.parse(ctx.etd);
  if (!Number.isFinite(stdMs) || !Number.isFinite(etdMs)) return null;
  const timeZone = zoneFor(ctx.originIata);
  const [y, m, d] = utcIsoToLocal(new Date(stdMs).toISOString(), timeZone).slice(0, 10).split('-').map(Number) as [
    number,
    number,
    number,
  ];
  const threshold = wallToUtcMs(Date.UTC(y, m - 1, d + 1, ctx.afterLocalHour), timeZone);
  const hotel = ctx.enabled && etdMs >= threshold;
  return {
    hotel,
    thresholdUtc: new Date(threshold).toISOString(),
    timeZone,
    reason: !ctx.enabled ? 'DISABLED' : hotel ? 'NEXT_DAY_AFTER_HOUR' : 'BEFORE_THRESHOLD',
  };
}
